import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, DollarSign, Plus, CreditCard, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { demoPayments } from '../data/dummyData';
import { AinaaBadge } from '../components/AinaaBadge';
import Breadcrumbs from '../components/Breadcrumbs';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const statusIcon = {
  paid: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={2} />,
  pending: <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" strokeWidth={2} />,
  overdue: <AlertCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400" strokeWidth={2} />,
};

export default function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = demoPayments.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.recipient.toLowerCase().includes(search.toLowerCase()) || (p.ainaa_id && p.ainaa_id.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totals = {
    paid: demoPayments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
    pending: demoPayments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0),
    overdue: demoPayments.filter(p => p.status === 'overdue').reduce((s, p) => s + p.amount, 0),
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="payments-page">
      <Breadcrumbs />
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Track all payments and invoices.</p>
        </div>
        <Button size="sm" className="gap-2 h-9" data-testid="new-payment-btn"><Plus className="w-4 h-4" strokeWidth={2} /> New Payment</Button>
      </motion.div>

      {/* Summary */}
      <motion.div variants={fadeUp} className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Paid', value: totals.paid, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Pending', value: totals.pending, color: 'text-amber-600 dark:text-amber-400' },
          { label: 'Overdue', value: totals.overdue, color: 'text-red-600 dark:text-red-400' },
        ].map(t => (
          <div key={t.label} className="p-4 rounded-xl border border-border/50 bg-card">
            <p className="text-[10px] text-muted-foreground mb-1">{t.label}</p>
            <p className={`font-heading text-xl font-semibold ${t.color}`}>₹{(t.value / 100000).toFixed(1)}L</p>
          </div>
        ))}
      </motion.div>

      <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search payments..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-payments-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9" data-testid="payment-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      <motion.div variants={fadeUp} className="space-y-2">
        {filtered.map(p => (
          <div key={p.payment_id} className="flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:border-border transition-all" data-testid={`payment-${p.payment_id}`}>
            {statusIcon[p.status]}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.title}</p>
              <p className="text-[10px] text-muted-foreground">To {p.recipient} · Due {p.due_date}</p>
              {p.ainaa_id && <span className="text-[10px] font-mono text-muted-foreground/70">{p.ainaa_id}</span>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-heading font-semibold">₹{(p.amount / 1000).toFixed(0)}K</p>
              <Badge
                variant={p.status === 'paid' ? 'default' : p.status === 'overdue' ? 'destructive' : 'secondary'}
                className="text-[9px] h-4 px-1 capitalize"
              >
                {p.status}
              </Badge>
            </div>
          </div>
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <CreditCard className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={2} />
          <p className="text-sm text-muted-foreground">No payments found</p>
        </div>
      )}
    </motion.div>
  );
}
